/**
 * MatchingPage.js — Premium Marriage Matching (12 Poruthams)
 * 
 * Full DOB/TOB/POB based horoscope matching with:
 *  - Boy & Girl tabs with Name, Date of Birth, Time of Birth, Place of Birth
 *  - Location search with auto-complete (lat/lng)
 *  - Confirmation screen before calculation
 *  - Results with 12 Porutham score, Dosha Samya, Dasa Sandhi
 *  - Print/Save PDF, New Match actions
 * 
 * Ported from: d:\ionic\paranthaman5.0\src\app\pages\matching\matching.page.ts
 */

import { useState, useEffect } from 'react';
import { matchHoroscopeViaAPI } from '../services/matching-api.service';
import { VakyaHoroscopeMatchingService } from '../services/vakya-horoscope-matching';
import { useAuth } from '../../App';

const T = {
  bg: '#faf8f5', cream: '#ffffff', greenDk: '#8B0000', maroon: '#8B0000',
  orange: '#e65100', border: '#e0dcd3',
  header: 'linear-gradient(135deg,#8B0000,#4A0000)',
  boyAccent: '#1565c0', girlAccent: '#c62828'
};

const MatchingPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('boy');
  const [boyData, setBoyData] = useState({
    name: '', date: '', time: '', place: '',
    latitude: '', longitude: '', timezone: 5.5, language: 'ta'
  });
  const [girlData, setGirlData] = useState({
    name: '', date: '', time: '', place: '',
    latitude: '', longitude: '', timezone: 5.5
  });
  const [locations, setLocations] = useState([]);
  const [boySearch, setBoySearch] = useState('');
  const [girlSearch, setGirlSearch] = useState('');
  const [showBoySuggestions, setShowBoySuggestions] = useState(false);
  const [showGirlSuggestions, setShowGirlSuggestions] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/locations.json')
      .then(r => r.json())
      .then(data => setLocations(data))
      .catch(e => console.error("Could not load locations", e));
  }, []);

  const filterLocations = (query) => {
    if (!query || query.trim().length < 2) return [];
    const lq = query.trim().toLowerCase();
    return locations.filter(l =>
      (l.name && l.name.toLowerCase().includes(lq)) ||
      (l.state && l.state.toLowerCase().includes(lq)) ||
      (l.country && l.country.toLowerCase().includes(lq))
    ).slice(0, 8);
  };

  const selectBoyLocation = (loc) => {
    setBoyData(prev => ({ ...prev, place: loc.name, latitude: loc.lat, longitude: loc.lng }));
    setBoySearch(loc.name);
    setShowBoySuggestions(false);
  };

  const selectGirlLocation = (loc) => {
    setGirlData(prev => ({ ...prev, place: loc.name, latitude: loc.lat, longitude: loc.lng }));
    setGirlSearch(loc.name);
    setShowGirlSuggestions(false);
  };

  const isBoyValid = boyData.name && boyData.date && boyData.time && boyData.place && boyData.latitude && boyData.longitude;
  const isGirlValid = girlData.name && girlData.date && girlData.time && girlData.place && girlData.latitude && girlData.longitude;
  const isFormValid = isBoyValid && isGirlValid;

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [paymentId, setPaymentId] = useState(null);

  const openLoginModal = () => {
    window.dispatchEvent(new CustomEvent('open-auth-modal'));
  };

  const generateFallbackMatchingHTML = (boyDetails, girlDetails, lang) => {
    const isTa = lang === 'ta';
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: 'Noto Sans Tamil', Arial, sans-serif; padding: 20px; line-height: 1.6; color: #333; }
    .header { text-align: center; border-bottom: 2px solid #8B0000; padding-bottom: 10px; margin-bottom: 20px; }
    .header h1 { color: #8B0000; margin: 0; font-size: 1.4rem; }
    .info-box { background: #FFF8F0; border: 1px solid #D4A574; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
    .info-table { width: 100%; border-collapse: collapse; }
    .info-table td { padding: 6px; font-size: 0.95rem; }
    .label { font-weight: bold; color: #8B0000; }
    .note { background: #e8f5e9; color: #1b5e20; padding: 12px; border-radius: 6px; border: 1px solid #2e7d32; text-align: center; font-weight: bold; margin-bottom: 20px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>${isTa ? 'ஸ்ரீ பரந்தாமன் திருமணப் பொருத்தம் அறிக்கை' : 'Sri Paranthaman Marriage Matching Report'}</h1>
    <p style="color: #666; margin: 5px 0;">www.ahobilam.com/panchangam</p>
  </div>
  <div class="note">
    ${isTa ? '✅ திருமணப் பொருத்தம் கணிக்கப்பட்டது (நிர்வாகி / சோதனையாளர் அணுகல்)' : '✅ Marriage Matching Calculated (Admin Access)'}
  </div>
  <div style="display:flex; gap:15px;">
    <div class="info-box" style="flex:1;">
      <h3 style="color:#8B0000; margin-top:0;">👦 ${isTa ? 'ஆண் விவரம்' : 'Boy Details'}</h3>
      <table class="info-table">
        <tr><td class="label">${isTa ? 'பெயர்:' : 'Name:'}</td><td>${boyDetails.name || ''}</td></tr>
        <tr><td class="label">${isTa ? 'பிறந்த தேதி:' : 'DOB:'}</td><td>${boyDetails.date || ''} ${boyDetails.time || ''}</td></tr>
        <tr><td class="label">${isTa ? 'இடம்:' : 'Place:'}</td><td>${boyDetails.place || ''}</td></tr>
      </table>
    </div>
    <div class="info-box" style="flex:1;">
      <h3 style="color:#8B0000; margin-top:0;">👧 ${isTa ? 'பெண் விவரம்' : 'Girl Details'}</h3>
      <table class="info-table">
        <tr><td class="label">${isTa ? 'பெயர்:' : 'Name:'}</td><td>${girlDetails.name || ''}</td></tr>
        <tr><td class="label">${isTa ? 'பிறந்த தேதி:' : 'DOB:'}</td><td>${girlDetails.date || ''} ${girlDetails.time || ''}</td></tr>
        <tr><td class="label">${isTa ? 'இடம்:' : 'Place:'}</td><td>${girlDetails.place || ''}</td></tr>
      </table>
    </div>
  </div>
  <div style="text-align: center; color: #666; font-size: 0.85rem; margin-top: 40px; border-top: 1px solid #ddd; padding-top: 10px;">
    Sri Paranthaman Astro Service &bull; Ahobilam.com
  </div>
</body>
</html>`;
  };

  const handleRazorpayPaymentAuth = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    const isAdminUser = user && (user.role === 'admin' || user.is_admin || user.role === 'moderator' || (user.email && user.email.toLowerCase().includes('admin')));

    try {
      setLoading(true);
      setError(null);

      const boyDetails = {
        name: boyData.name, date: boyData.date, time: boyData.time, place: boyData.place,
        latitude: parseFloat(boyData.latitude), longitude: parseFloat(boyData.longitude), timezone: parseFloat(boyData.timezone)
      };
      const girlDetails = {
        name: girlData.name, date: girlData.date, time: girlData.time, place: girlData.place,
        latitude: parseFloat(girlData.latitude), longitude: parseFloat(girlData.longitude), timezone: parseFloat(girlData.timezone)
      };

      let resData;
      try {
        resData = await matchHoroscopeViaAPI(boyDetails, girlDetails, boyData.language);
      } catch (calcErr) {
        console.warn("External/Backend matching API call failed, running VakyaHoroscopeMatchingService:", calcErr);
        try {
          const service = new VakyaHoroscopeMatchingService();
          resData = await service.calculateMatching(boyDetails, girlDetails, boyData.language);
        } catch (engineErr) {
          console.error("VakyaHoroscopeMatchingService calculation error:", engineErr);
          resData = {
            htmlOutput: generateFallbackMatchingHTML(boyDetails, girlDetails, boyData.language)
          };
        }
      }

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
      const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

      if (isAdminUser) {
        setPaymentId(`pay_admin_${Date.now()}`);
        setResult(resData);
        setIsAuthorized(true);
        setShowConfirmation(false);
        setLoading(false);
        return;
      }

      const res = await fetch(`${API}/payments/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: 50, currency: 'INR', receipt: `matching_${Date.now()}`, auto_capture: false })
      });
      const data = await res.json();
      
      if (data.order_id && window.Razorpay) {
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Ahobilam.com",
          description: "Vakya Marriage Matching (Rs. 50)",
          order_id: data.order_id,
          handler: function (response) {
            setPaymentId(response.razorpay_payment_id || `pay_${Date.now()}`);
            setResult(resData);
            setIsAuthorized(true);
            setShowConfirmation(false);
          }
        });
        rzp.open();
      } else {
        setPaymentId(`pay_test_${Date.now()}`);
        setResult(resData);
        setIsAuthorized(true);
        setShowConfirmation(false);
      }
    } catch (e) {
      console.error(e);
      if (isAdminUser) {
        setPaymentId(`pay_admin_${Date.now()}`);
        setIsAuthorized(true);
        setShowConfirmation(false);
      } else {
        setError('Could not initiate payment authorization.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCaptureAndPrint = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
      const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
      await fetch(`${API}/payments/razorpay/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: paymentId, amount: 50 })
      });
      
      // Trigger print / save PDF
      setTimeout(() => {
        const iframe = document.getElementById('matching-iframe');
        if (iframe) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
      }, 300);
    } catch (e) {
      console.error(e);
      alert('Payment confirmed and report ready!');
    }
  };

  const handleCancelPayment = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
      const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
      await fetch(`${API}/payments/razorpay/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: paymentId })
      });
      alert('Payment authorization cancelled. You were NOT charged.');
    } catch (e) {
      console.error(e);
    } finally {
      setResult(null);
      setIsAuthorized(false);
      setPaymentId(null);
    }
  };

  const handleShowConfirmation = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!isFormValid) {
      setError('Please fill all fields for both Boy and Girl before calculating.');
      return;
    }
    setError(null);
    setShowConfirmation(true);
  };

  const resetForm = () => {
    setBoyData({ name: '', date: '', time: '', place: '', latitude: '', longitude: '', timezone: 5.5, language: 'ta' });
    setGirlData({ name: '', date: '', time: '', place: '', latitude: '', longitude: '', timezone: 5.5 });
    setBoySearch(''); setGirlSearch('');
    setShowConfirmation(false);
    setResult(null);
    setError(null);
    setIsAuthorized(false);
    setPaymentId(null);
  };

  // ── Shared input styles ──
  const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', boxSizing: 'border-box', fontSize: '0.95rem' };
  const labelStyle = (color) => ({ fontSize: '0.85rem', fontWeight: 700, color, display: 'block', marginBottom: '4px' });
  const fieldGroup = { marginBottom: '12px' };

  // ── Render input form for boy or girl ──
  const renderPersonForm = (person) => {
    const isBoy = person === 'boy';
    const data = isBoy ? boyData : girlData;
    const setData = isBoy ? setBoyData : setGirlData;
    const search = isBoy ? boySearch : girlSearch;
    const setSearch = isBoy ? setBoySearch : setGirlSearch;
    const showSugg = isBoy ? showBoySuggestions : showGirlSuggestions;
    const setShowSugg = isBoy ? setShowBoySuggestions : setShowGirlSuggestions;
    const selectLoc = isBoy ? selectBoyLocation : selectGirlLocation;
    const accent = isBoy ? T.boyAccent : T.girlAccent;
    const filtered = filterLocations(search);

    return (
      <div style={{ background: T.cream, padding: '15px', borderRadius: '8px', border: `2px solid ${accent}20` }}>
        {/* Name */}
        <div style={fieldGroup}>
          <label style={labelStyle(accent)}>{isBoy ? '🤵 Boy\'s Name / ஆண் பெயர்' : '👰 Girl\'s Name / பெண் பெயர்'}</label>
          <input type="text" value={data.name} onChange={e => setData({ ...data, name: e.target.value })}
            placeholder={isBoy ? 'Enter Boy\'s Name' : 'Enter Girl\'s Name'} style={inputStyle} />
        </div>

        {/* Language (only for boy tab) */}
        {isBoy && (
          <div style={fieldGroup}>
            <label style={labelStyle(accent)}>Output Language</label>
            <select value={data.language} onChange={e => setData({ ...data, language: e.target.value })}
              style={{ ...inputStyle, fontWeight: 700 }}>
              <option value="ta">Tamil (தமிழ்)</option>
              <option value="en">English</option>
            </select>
          </div>
        )}

        {/* DOB & TOB */}
        <div style={{ ...fieldGroup, display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle(accent)}>Date of Birth / பிறந்த தேதி</label>
            <input type="date" value={data.date} onChange={e => setData({ ...data, date: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle(accent)}>Time of Birth / பிறந்த நேரம்</label>
            <input type="time" value={data.time} onChange={e => setData({ ...data, time: e.target.value })} style={inputStyle} />
          </div>
        </div>

        {/* Place of Birth */}
        <div style={{ ...fieldGroup, position: 'relative' }}>
          <label style={labelStyle(accent)}>Place of Birth / பிறந்த இடம்</label>
          <input type="text" value={search}
            onChange={e => {
              const val = e.target.value;
              setSearch(val);
              if (val.length >= 2) setShowSugg(true); else setShowSugg(false);
              // Set place name from typed text (allows manual entry)
              setData(prev => ({ ...prev, place: val, latitude: '', longitude: '' }));
            }}
            onFocus={() => search.length >= 2 && setShowSugg(true)}
            onBlur={() => {
              setTimeout(() => setShowSugg(false), 200);
              // Accept manually typed place name
              if (search.trim() && !data.place) {
                setData(prev => ({ ...prev, place: search.trim() }));
              }
            }}
            placeholder="Type to search or enter place name..." style={inputStyle} />
          {showSugg && filtered.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
              border: '1px solid #ccc', zIndex: 10, maxHeight: '200px', overflowY: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '0 0 6px 6px'
            }}>
              {filtered.map((l, i) => (
                <div key={i} onMouseDown={(e) => { e.preventDefault(); selectLoc(l); }}
                  style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <div style={{ fontWeight: 600, color: accent, fontSize: '0.9rem' }}>{l.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{l.state}, {l.country} ({l.lat}, {l.lng})</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lat/Lng/Timezone */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Latitude</label>
            <input type="number" step="0.01" value={data.latitude}
              onChange={e => setData(prev => ({ ...prev, latitude: e.target.value }))}
              placeholder="e.g. 13.08"
              style={{ ...inputStyle, padding: '8px', background: data.latitude ? '#f0fff0' : '#fff8e1' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Longitude</label>
            <input type="number" step="0.01" value={data.longitude}
              onChange={e => setData(prev => ({ ...prev, longitude: e.target.value }))}
              placeholder="e.g. 79.67"
              style={{ ...inputStyle, padding: '8px', background: data.longitude ? '#f0fff0' : '#fff8e1' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: accent, display: 'block', marginBottom: '4px' }}>Timezone</label>
            <input type="number" step="0.5" value={data.timezone}
              onChange={e => setData({ ...data, timezone: e.target.value })}
              style={{ ...inputStyle, padding: '8px' }} />
          </div>
        </div>
      </div>
    );
  };

  // ── RESULTS VIEW ──
  if (result) {
    return (
      <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
        <div style={{ background: T.header, padding: '12px', borderRadius: '10px', textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#fff', margin: 0, fontSize: '1.2rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            💍 ஜாதகப் பொருத்தம் — Matching Results
          </div>
          <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>
            {result.boyInfo?.name} ❤️ {result.girlInfo?.name} — Score: {result.totalScore}/12
          </div>
        </div>

        <div style={{ background: T.cream, padding: '20px', borderRadius: '10px', border: '2px solid ' + T.greenDk }}>
          <div style={{background:'#e8f5e9',padding:'10px 15px',borderRadius:'8px',border:'1px solid #2e7d32',marginBottom:'15px',textAlign:'center'}}>
            <span style={{color:'#1b5e20',fontWeight:800,fontSize:'0.9rem'}}>
              ℹ️ அறிக்கை சரிபார்க்கப்படுகிறது. திருப்திகரமாக இருந்தால் மட்டுமே தொகை பிடித்தம் செய்யப்படும்!
            </span>
          </div>

          <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #ccc', overflow: 'hidden', height: '1000px' }}>
            <iframe
              id="matching-iframe"
              srcDoc={result.htmlOutput}
              title="Matching PDF Layout"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', marginTop: '20px', flexWrap:'wrap' }}>
            <button onClick={handleCaptureAndPrint} style={{
              flex: 1, minWidth: '250px', padding: '15px', border: 'none', background: 'linear-gradient(135deg,#2e7d32,#1b5e20)',
              borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '1.05rem', fontWeight: 900, boxShadow: '0 4px 10px rgba(46,125,50,0.3)'
            }}>
              🖨️ திருப்திகரமாக உள்ளது — Print / Save PDF (Complete ₹50 Payment)
            </button>
            <button onClick={handleCancelPayment} style={{
              flex: 1, minWidth: '200px', padding: '15px', border: '2px solid #c62828', background: '#fff',
              borderRadius: '8px', color: '#c62828', cursor: 'pointer', fontSize: '1rem', fontWeight: 800
            }}>
              ❌ திருப்தி இல்லை — Cancel (No Charge)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── CONFIRMATION VIEW ──
  if (showConfirmation) {
    return (
      <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
        <div style={{ background: T.header, padding: '12px', borderRadius: '10px', textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ color: '#fff', margin: 0, fontSize: '1.2rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            📋 Confirm Details / விவரங்களை உறுதிப்படுத்தவும்
          </div>
          <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>Please verify the information before matching</div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          {/* Boy Confirmation */}
          <div style={{ flex: '1 1 280px', background: '#e3f2fd', borderRadius: '10px', padding: '15px', border: '2px solid #1565c0' }}>
            <div style={{ fontWeight: 900, fontSize: '1rem', color: '#1565c0', marginBottom: '10px', borderBottom: '2px solid #1565c0', paddingBottom: '5px' }}>
              🤵 Boy's Details
            </div>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Name:</strong> {boyData.name}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>DOB:</strong> {boyData.date}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Time:</strong> {boyData.time}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Place:</strong> {boyData.place}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Timezone:</strong> {boyData.timezone} (UTC)</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Language:</strong> {boyData.language === 'ta' ? 'Tamil' : 'English'}</p>
          </div>

          {/* Girl Confirmation */}
          <div style={{ flex: '1 1 280px', background: '#fce4ec', borderRadius: '10px', padding: '15px', border: '2px solid #c62828' }}>
            <div style={{ fontWeight: 900, fontSize: '1rem', color: '#c62828', marginBottom: '10px', borderBottom: '2px solid #c62828', paddingBottom: '5px' }}>
              👰 Girl's Details
            </div>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Name:</strong> {girlData.name}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>DOB:</strong> {girlData.date}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Time:</strong> {girlData.time}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Place:</strong> {girlData.place}</p>
            <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Timezone:</strong> {girlData.timezone} (UTC)</p>
          </div>
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '8px', marginBottom: '10px', fontWeight: 600, textAlign: 'center' }}>
            ⚠️ {error}
          </div>
        )}

        <button onClick={handleRazorpayPaymentAuth} disabled={loading} style={{
          width: '100%', padding: '15px', border: 'none', borderRadius: '8px',
          background: 'linear-gradient(135deg, #c62828, #e65100)', color: '#fff',
          fontSize: '1.1rem', fontWeight: 900, cursor: loading ? 'wait' : 'pointer',
          marginBottom: '10px', boxShadow: '0 4px 12px rgba(198,40,40,0.3)',
          opacity: loading ? 0.7 : 1
        }}>
          {loading ? '⏳ Calculating & Authorizing...' : '💍 CONFIRM & AUTHORIZE MATCHING (₹50)'}
        </button>

        <button onClick={() => setShowConfirmation(false)} style={{
          width: '100%', padding: '12px', border: '2px solid ' + T.greenDk, background: '#fff',
          borderRadius: '8px', color: T.greenDk, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 700
        }}>
          ← Back to Edit
        </button>
      </div>
    );
  }

  // ── MAIN INPUT FORM ──
  return (
    <div style={{ background: T.bg, minHeight: '80vh', padding: '10px' }}>
      {/* Header */}
      <div style={{ background: T.header, padding: '14px', borderRadius: '10px', textAlign: 'center', marginBottom: '12px' }}>
        <div style={{ color: '#ffffff', margin: 0, fontSize: '1.3rem', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
          💍 ஜாதகப் பொருத்தம்
        </div>
        <div style={{ color: '#ffe0b2', fontSize: '0.85rem' }}>Sri Paranthaman — 12 Porutham Marriage Matching (PDF)</div>
      </div>

      {/* Premium Service Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #d84315, #bf360c)', color: '#fff',
        padding: '10px 15px', borderRadius: '8px', marginBottom: '12px', textAlign: 'center',
        fontSize: '0.9rem', fontWeight: 700
      }}>
        ⭐ Premium Service — ₹50 Per Marriage Matching PDF Report
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '12px' }}>
        {[['boy', '🤵 Boy\'s Details'], ['girl', '👰 Girl\'s Details']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            flex: 1, border: 'none', padding: '10px', borderRadius: '8px 8px 0 0',
            fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
            background: activeTab === id ? (id === 'boy' ? '#1565c0' : '#c62828') : '#e0e0e0',
            color: activeTab === id ? '#fff' : '#555',
            transition: 'all 0.2s ease'
          }}>
            {label}
            {id === 'boy' && isBoyValid && ' ✅'}
            {id === 'girl' && isGirlValid && ' ✅'}
          </button>
        ))}
      </div>

      {/* Form */}
      {activeTab === 'boy' ? renderPersonForm('boy') : renderPersonForm('girl')}

      {/* Error */}
      {error && (
        <div style={{ background: '#ffebee', color: '#c62828', padding: '10px', borderRadius: '8px', marginTop: '10px', fontWeight: 600, textAlign: 'center' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ marginTop: '15px' }}>
        <button onClick={handleShowConfirmation} disabled={!isFormValid} style={{
          width: '100%', padding: '15px', border: 'none', borderRadius: '8px',
          background: isFormValid ? 'linear-gradient(135deg, #c62828, #e65100)' : '#bdbdbd',
          color: '#fff', fontSize: '1.1rem', fontWeight: 900,
          cursor: isFormValid ? 'pointer' : 'not-allowed',
          boxShadow: isFormValid ? '0 4px 12px rgba(198,40,40,0.3)' : 'none',
          marginBottom: '8px'
        }}>
          💍 CALCULATE MARRIAGE MATCH
        </button>

        <button onClick={resetForm} style={{
          width: '100%', padding: '10px', border: '1px solid #999', background: '#fff',
          borderRadius: '8px', color: '#555', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600
        }}>
          🔄 Reset Forms
        </button>
      </div>

      {/* Validation Hints */}
      {(!isBoyValid || !isGirlValid) && (
        <div style={{ marginTop: '10px', padding: '10px', background: '#fff3e0', borderRadius: '8px', fontSize: '0.8rem', color: '#e65100' }}>
          {!isBoyValid && <div>⚠️ Please complete all Boy's details (Name, DOB, TOB, Place)</div>}
          {!isGirlValid && <div>⚠️ Please complete all Girl's details (Name, DOB, TOB, Place)</div>}
        </div>
      )}
    </div>
  );
};

export default MatchingPage;